import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { RocketLaunchOutlined } from "@mui/icons-material";
import { ContainedButton, TextButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import { StyledDatePicker } from "../../common/StyledDatePicker";

const PublishOptionsModal = ({
  open,
  onClose,
  onPublishNow,
  onSchedule,
}) => {
  const [publishOption, setPublishOption] = useState(null);
  const [publishDate, setPublishDate] = useState(null);
  const [publishOptionError, setPublishOptionError] = useState(false);
  const [publishScheduleError, setPublishScheduleError] = useState(false);

  const handleSubmit = () => {
    if (!publishOption) {
      setPublishOptionError(true);
      return;
    }

    if (publishOption === "publish") {
      onPublishNow();
      return;
    }

    if (publishOption === "schedule") {
      if (!publishDate) {
        setPublishScheduleError(true);
        return;
      }

      onSchedule(publishDate);
    }
  };

  const renderSubtitle = () => {
    if (!publishScheduleError && !publishOptionError) {
        return "Choose whether you'd like to publish it now or schedule it for later."
    } else if (publishOptionError) {
        return (
            <span style={{ color: Colors.redColor }}>
                Please select a publishing option.
            </span>
        )
    } else if (publishScheduleError) {
        return (
            <span style={{ color: Colors.redColor }}>
                Please choose a date and time to publish.
            </span>
        )
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      }}
    >
      <DialogContent>
        <Box textAlign="center" p={3}>
          <RocketLaunchOutlined
            sx={{ fontSize: 60, color: Colors.primaryColor }}
          />

          <h3 style={{ fontSize: "24px", marginBottom: 8 }}>
            How would you like to publish this article?
          </h3>

          <p style={{ marginTop: 0 }}>
            {renderSubtitle()}
          </p>

          <Box
            mt={3}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            width="100%"
            maxWidth={600}
            mx="auto"
          >
            {[
              {
                key: "publish",
                title: "Publish Now",
                description:
                  "Your article will go live immediately once you confirm.",
              },
              {
                key: "schedule",
                title: "Schedule Publish",
                description:
                  "Choose a date and time for your article to go live automatically.",
              },
            ].map(({ key, title, description }) => (
              <Paper
                key={key}
                elevation={publishOption === key ? 4 : 1}
                onClick={() => {
                  setPublishOption(key);
                  setPublishOptionError(false);
                }}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  p: 2,
                  width: "100%",
                  borderRadius: "12px",
                  textAlign: "left",
                  border:
                    publishOption === key
                      ? `2px solid ${Colors.primaryColor}`
                      : "1px solid #ddd",
                  boxShadow:
                    publishOption === key
                      ? "0px 4px 12px rgba(0,0,0,0.1)"
                      : "0px 2px 4px rgba(0,0,0,0.05)",
                  "&:hover": {
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "18px", marginBottom: "4px" }}
                >
                  {title}
                </Typography>
                <Typography variant="body2">{description}</Typography>
              </Paper>
            ))}
          </Box>

          {publishOption === "schedule" && (
            <Box
              mt={3}
              width="100%"
              mx="auto"
              display="flex"
              justifyContent="center"
            >
              <StyledDatePicker
                fullWidth
                label="Select a publish date"
                views={["year", "month", "day", "hours"]}
                value={publishDate}
                onChange={(newDate) => {
                  setPublishDate(newDate);
                  setPublishOptionError(false);
                  setPublishScheduleError(false);
                }}
                error={publishScheduleError}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "Choose a date",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <TextButton onClick={onClose}>Cancel</TextButton>
        <ContainedButton onClick={handleSubmit}>
            {publishOption === "schedule" ? "Schedule" : "Publish Now"}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default PublishOptionsModal;
